import React, { useState, useEffect } from 'react';
import Autosuggest, { SuggestionsFetchRequestedParams } from 'react-autosuggest';
import { Field, useFormikContext } from 'formik';
import { apiClient } from "../../../utils/ApiClient";
import { Option } from "./ElogSelect";
import './css/ClientInput.css';
import { Link } from "react-router-dom";

interface ClientInputProps {
    storageKey?: string;
}

interface FieldRenderProps {
    field: {
        name: string;
        value: string;
        onChange: (e: React.ChangeEvent<any>) => void;
        onBlur: () => void;
        onFocus: () => void;
    };
}

interface Comment {
    id: number;
    comment: string;
}

const ClientInput: React.FC<ClientInputProps> = ({ storageKey }) => {
    storageKey = storageKey || 'clientInput';
    const formik = useFormikContext<any>();
    const [value, setValue] = useState<string>('');
    const [clientRestored, setClientRestored] = useState(false);

    const [suggestions, setSuggestions] = useState<Option[]>([]);
    const [clientContacts, setClientContacts] = useState<Option[]>([]);
    const [comments, setComments] = useState<Comment[]>([]);
    const [comment, setComment] = useState<string>('');
    const [clientId, setClientId] = useState<number | null>(null);

    useEffect(() => {
        const stored = sessionStorage.getItem(storageKey || 'clientInput');
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.client) {
                setValue(parsed.client);
                formik.setFieldValue('client', parsed.client);
                fetchClientContacts(parsed.client);
            }
        }
    }, []);

    useEffect(() => {
        const key = storageKey ?? 'clientInput';
        let parsed = sessionStorage.getItem(key) ? JSON.parse(sessionStorage.getItem(key)!) : {};
        parsed.client = value;
        sessionStorage.setItem(key, JSON.stringify(parsed));
    }, [value]);

    useEffect(() => {
        const clientValue = formik.values.client;
        if (clientValue) {
            fetchClientContacts(clientValue);
        }
    }, [formik.values.client]);

    const fetchClients = async (inputValue: string) => {
        try {
            const apiResponse = await apiClient.get(`suggest/client?search=${encodeURIComponent(inputValue)}`);
            const clients: Option[] = apiResponse.data.map((client: { id: number; name: string }) => ({
                label: client.name,
                value: client.id.toString(),
            }));
            fetchClientContacts(inputValue);
            setSuggestions(clients);
        } catch (error) {
            console.error('Fetch clients error:', error);
        }
    };

    const onSuggestionsFetchRequested = ({ value }: SuggestionsFetchRequestedParams) => {
        if (value.length > 1) {
            fetchClients(value);
        } else {
            setSuggestions([]);
        }
    };

    const onSuggestionsClearRequested = () => {
        setSuggestions([]);
    };

    const onSuggestionSelected = (event: React.FormEvent<any>, { suggestion }: { suggestion: Option }) => {
        setValue(suggestion.label);
        formik.setFieldValue('client', suggestion.label);
        fetchClientContacts(suggestion.label);
    };

    const renderSuggestion = (suggestion: Option) => (
        <div>
            {suggestion.label}
        </div>
    );

    const fetchClientContacts = async (selectedClientName: string) => {
        try {
            const apiResponse = await apiClient.get(`client-contacts/client?clientName=${encodeURIComponent(selectedClientName)}`);
            const contacts: Option[] = apiResponse.data.map((contact: { id: number; name: string }) => ({
                label: contact.name,
                value: contact.id.toString(),
            }));
            const comments: Comment[] = apiResponse.data.map((contact: { id: number; comment: string }) => ({
                comment: contact.comment,
                id: contact.id,
            }));
            setClientContacts(contacts);
            setComments(comments);

            if (contacts.length > 0 && !formik?.values?.clientContact) {
                formik.setFieldValue('clientContact', contacts[0].value);
                setComment(comments[0].comment);
            } else {
                const clientContactId = formik?.values?.clientContact;
                const contactComment = comments.find(contact => contact.id === clientContactId)?.comment;
                if (contactComment) {
                    setComment(contactComment);
                }
            }

            if (contacts.length > 0) {
                console.log(apiResponse.data[0]);
                setClientId(apiResponse.data[0].client_id);
            }
        } catch (error) {
            console.error('Fetch client contacts error:', error);
        }
    };

    return (
        <>
            <tr>
                <th>Klient*:</th>
                <td>
                    <Field
                        name="client"
                        render={({ field }: FieldRenderProps) => (
                            <Autosuggest
                                suggestions={suggestions}
                                onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                                onSuggestionsClearRequested={onSuggestionsClearRequested}
                                onSuggestionSelected={onSuggestionSelected}
                                getSuggestionValue={(suggestion: Option) => suggestion.label}
                                renderSuggestion={renderSuggestion}
                                inputProps={{
                                    ...field,
                                    value: field.value || value,
                                    onChange: (_, { newValue }) => {
                                        setValue(newValue);
                                        formik.setFieldValue(field.name, newValue);
                                    },
                                    onBlur: undefined,
                                }}
                                theme={{
                                    container: 'react-autosuggest__container',
                                    suggestionsContainer: suggestions.length > 0 ? 'react-autosuggest__suggestions-container visible' : 'react-autosuggest__suggestions-container',
                                    suggestionsList: 'react-autosuggest__suggestions-list',
                                    suggestion: 'react-autosuggest__suggestion',
                                    suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                                }}
                            />
                        )}
                    />
                </td>
            </tr>
            <tr>
                <th>Kliendi kontakt*:</th>
                <td>
                    <Field name={`clientContact`}>
                        {({ field }: FieldRenderProps) => (
                            <>
                                <select {...field}
                                        onChange={(event) => {
                                            field.onChange(event);
                                        }}
                                >
                                    {clientContacts.map((contact) => (
                                        <option key={contact.value} value={contact.value}>
                                            {contact.label}
                                        </option>
                                    ))}
                                </select>
                                <Link to={`/clients/${clientId}/contacts/new`} target={'_blank'}>Lisa uus</Link>
                            </>
                        )}
                    </Field>
                    <div style={{ fontWeight: 'bold' }} id="client_contact_comment">
                        {comment}
                    </div>
                </td>
            </tr>
        </>
    );
};

export default ClientInput;